'use client';

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils/classnameVariants';
import { focusRingClasses } from '../../lib/utils/constants/accessbilityClasses';
import { StateIcon } from '../../lib/utils/forms';
import { useDialogPortalContainer } from '../../lib/utils/hooks/useDialogPortalContainer';
import { FormLabel } from '../atoms/FormLabel';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DropdownOption } from './Dropdown';
import { Description } from './TextField';

export interface DropdownSearchOption {
  text: string;
  value: string;
}

export interface DropdownSearchProps {
  label?: string;
  placeholder: string;
  searchPlaceholder: string;
  choseCustomText?: string;
  suggestCustomText?: string;
  noResultsText: string;
  options: DropdownSearchOption[];
  onChange: (value?: DropdownOption) => void;
  className?: string;
  error?: string;
  description?: string;
  disabled?: boolean;
  value?: string;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  /**
   * Whether the user can enter a custom value not present in the provided options.
   * Defaults to true for backward compatibility.
   */
  allowCustom?: boolean;
  /**
   * When true, shows a small loading animation
   */
  isLoading?: boolean;
  /**
   * If false, disables filtering against the input string. All options are shown as-is.
   * Defaults to true for backward compatibility.
   */
  filterAgainstInput?: boolean;
}

type Selected = { text: string; value: string } | null;

const DropdownSearch = forwardRef<HTMLDivElement, DropdownSearchProps>(
  (
    {
      label,
      placeholder,
      choseCustomText,
      suggestCustomText,
      noResultsText,
      searchPlaceholder,
      options,
      description,
      onChange,
      className,
      error,
      disabled,
      value,
      searchValue,
      onSearchValueChange,
      allowCustom = true,
      isLoading = false,
      filterAgainstInput = true,
    },
    ref,
  ) => {
    const [internalSelected, setInternalSelected] = useState<Selected>(null);
    const [open, setOpen] = useState(false);
    const [internalSearch, setInternalSearch] = useState('');
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [contentWidth, setContentWidth] = useState<number | undefined>(undefined);
    // When DropdownSearch is inside a Radix Dialog, portal PopoverContent INTO
    // the dialog element so Dialog's FocusScope considers it a child of its
    // container. This prevents FocusScope from stealing focus from CommandInput.
    // Outside a dialog, portalContainer stays undefined (= portal to body).
    const { wrapperRef, portalContainer } = useDialogPortalContainer<HTMLDivElement>();

    // Use controlled props if provided, otherwise use internal state
    // If value is is not in options, we allow custom value if allowCustom is true
    // Ensure every option has a value property
    const normalizedOptions = options.map(o => ({
      text: o.text,
      value: typeof o.value === 'string' ? o.value : o.text,
    }));

    const selected =
      value !== undefined
        ? normalizedOptions.find(o => o.value === value) || (allowCustom && value ? { text: value, value } : null)
        : internalSelected;
    const search = searchValue !== undefined ? searchValue : internalSearch;

    // Enhanced filter: all search words must be present in value, any order
    // Enhanced filter: all search words must match as substring of any word in value, any order
    const filterFn = useCallback(
      (value: string, search: string) => {
        if (!filterAgainstInput) return 1;
        const v = value.toLowerCase();
        const s = search.toLowerCase().trim();
        if (!s) return 1;
        const words = s.split(/\s+/).filter(Boolean);
        const valueWords = v.split(/\s+/);
        return words.every(word => valueWords.some(valWord => valWord.includes(word))) ? 1 : 0;
      },
      [filterAgainstInput],
    );

    const setSearch = (newSearch: string) => {
      if (onSearchValueChange) {
        onSearchValueChange(newSearch);
      } else {
        setInternalSearch(newSearch);
      }
    };

    const updateWidth = useCallback(() => {
      const w = triggerRef.current?.offsetWidth;
      if (w && w !== contentWidth) setContentWidth(w);
    }, [contentWidth]);

    useLayoutEffect(() => {
      updateWidth();
    }, [updateWidth]);

    useEffect(() => {
      if (!open) return;

      updateWidth();

      const el = triggerRef.current;
      const ro = new ResizeObserver(() => updateWidth());
      if (el) ro.observe(el);

      const onWinResize = () => updateWidth();
      window.addEventListener('resize', onWinResize);

      return () => {
        window.removeEventListener('resize', onWinResize);
        if (el) ro.unobserve(el);
        ro.disconnect();
      };
    }, [open, updateWidth]);

    const commit = useCallback(
      (item: { text: string; value: string }) => {
        if (value === undefined) {
          setInternalSelected(item);
        }
        onChange(item);
        setOpen(false);
        if (onSearchValueChange) {
          onSearchValueChange('');
        } else {
          setInternalSearch('');
        }
      },
      [onChange, value, onSearchValueChange],
    );

    const onSelectOption = (val: string) => {
      const option = options.find(o => o.value === val);
      if (option) {
        commit(option);
      } else if (allowCustom) {
        commit({ text: val, value: val });
      }
    };

    const tryCommitCustom = () => {
      if (!allowCustom) return;
      const trimmed = search.trim();
      if (!trimmed) return;
      const matchByValue = options.find(o => o.value.toLowerCase() === trimmed.toLowerCase());
      if (matchByValue) {
        commit(matchByValue);
      } else {
        commit({ text: trimmed, value: trimmed });
      }
    };

    const handleOpenChange = (isOpen: boolean) => {
      setOpen(isOpen);
      if (isOpen) {
        // Clear search input when opening
        if (onSearchValueChange) {
          onSearchValueChange('');
        } else {
          setInternalSearch('');
        }
      }
    };

    const clearSelected = () => {
      if (value === undefined) {
        setInternalSelected(null);
      }
      onChange(undefined);
      // Also clear search when clearing selection
      if (onSearchValueChange) {
        onSearchValueChange('');
      } else {
        setInternalSearch('');
      }
    };

    const buttonLabel = selected?.text ?? (open ? '' : placeholder);

    return (
      <div
        ref={node => {
          wrapperRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        className={className}
      >
        {label && <FormLabel title={label} className="pb-2" />}
        <Popover open={open} onOpenChange={disabled ? () => {} : handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              disabled={disabled}
              role="combobox"
              type="button"
              aria-label="openSearchableDropdown"
              aria-expanded={open}
              className={cn(
                'relative flex min-h-[42px] w-full items-center justify-between rounded-sm border border-black-100 bg-white px-3 py-2',
                'focus-visible:border-black-900 focus-visible:outline-none focus-visible:ring-0',
                open && 'border-black-900',
                disabled && 'cursor-not-allowed opacity-40',
              )}
              ref={triggerRef}
            >
              <p
                className={cn(
                  'truncate font-graphik',
                  !!error && 'pr-6',
                  selected?.text ? 'opacity-100' : 'opacity-50',
                )}
              >
                {buttonLabel}
              </p>

              {selected ? (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Clear selection"
                  onClick={clearSelected}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      clearSelected();
                    }
                  }}
                  className={cn(
                    'size-4 shrink-0 items-center rounded-xs stroke-2 hover:text-black-500',
                    focusRingClasses,
                  )}
                >
                  <XMarkIcon />
                </span>
              ) : (
                <MagnifyingGlassIcon className="size-4 stroke-2" />
              )}
              {!!error && StateIcon(!!error, false, 'right-[38px] size-6')}
            </button>
          </PopoverTrigger>

          {error && <Description className="mt-2 text-orange-900-semantic-error" description={error} />}
          {!error && description && <Description className="mt-2" description={description} />}

          <PopoverContent
            portalContainer={portalContainer}
            align="start"
            sideOffset={4}
            style={{ width: contentWidth }}
            className="rounded-xs border-black-900 bg-white p-0"
          >
            <Command filter={filterFn}>
              <div className="relative">
                <CommandInput
                  noIcon
                  className="pr-6 text-black-300"
                  placeholder={searchPlaceholder}
                  value={search}
                  onValueChange={setSearch}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      tryCommitCustom();
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setOpen(false);
                    }
                  }}
                />
                {!!search.trim() && (
                  <button
                    onClick={() => setSearch('')}
                    className={cn(
                      'absolute right-4 top-1/2 size-4 -translate-y-1/2 rounded-xs stroke-2 p-0 hover:text-black-500',
                    )}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSearch('');
                      }
                    }}
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
              <CommandList>
                <CommandGroup>
                  {(!filterAgainstInput || !search.trim()
                    ? normalizedOptions
                    : normalizedOptions.filter(opt => filterFn(opt.text, search) > 0)
                  ).map(opt => (
                    <CommandItem asChild key={opt.value} value={opt.text}>
                      <button
                        key={opt.value}
                        value={opt.text}
                        className={cn(
                          'w-full cursor-pointer rounded-xs px-2 py-1 text-left text-sm hover:bg-blue-50',
                          focusRingClasses,
                        )}
                        onClick={() => onSelectOption(opt.value)}
                      >
                        {opt.text}
                      </button>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandEmpty className="p-1 text-sm">
                  <p
                    className={cn(
                      'w-full justify-start rounded-xs px-2 py-1 text-center text-black-300',
                      isLoading && 'animate-pulse',
                    )}
                    aria-busy={isLoading ? true : undefined}
                  >
                    {noResultsText}
                  </p>
                </CommandEmpty>
                {allowCustom && suggestCustomText && choseCustomText && !!search.trim() && (
                  <div className="mb-1 flex justify-center px-1">
                    <p className="w-fit truncate rounded-xs px-1 pt-1 text-sm text-black-300">
                      {suggestCustomText.replace('{input}', `"${search.trim()}"`)}
                    </p>
                    <button
                      className="w-fit cursor-pointer truncate rounded-xs px-1 py-1 text-right text-sm text-black-900 underline hover:no-underline"
                      onClick={tryCommitCustom}
                    >
                      {choseCustomText.replace('{input}', `"${search.trim()}"`)}
                    </button>
                  </div>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

DropdownSearch.displayName = 'DropdownSearch';
export default DropdownSearch;
